<template>
    <modal
        :show="show"
        :loading="loading"
        :loading-text="loadingText"
        :title="title"
        :actions="actions"
        :freeze-body="freezeBody"
        @close="$emit('close')"
    >
        <div
            class="w-full max-w-2xl h-[24rem] flex flex-col items-center justify-center mx-auto"
        >
            <label
                class="relative border-dashed border-slate-300 hover:border-primary transition-all border-2 rounded-md p-4 flex flex-col items-center justify-center cursor-pointer min-h-[200px] gap-2 group/file-input w-full mx-auto"
            >
                <div
                    v-if="files.length === 0"
                    class="flex items-center gap-2 text-lg text-slate-500 group-hover/file-input:text-primary transition-all"
                >
                    <i class="fas fa-upload"></i>
                    <span>
                        {{ title }}
                    </span>
                </div>
                <div
                    v-if="files.length > 0"
                    class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 w-full max-h-full max-w-full mx-auto"
                >
                    <div
                        class="aspect-video border-2 border-slate-300 rounded-md overflow-hidden relative flex flex-col items-center justify-center bg-slate-50 max-h-full max-w-full mx-auto"
                        v-bind:class="{
                            'col-span-full': filePreviews.length === 1,
                            'col-span-1': filePreviews.length > 1,
                        }"
                        v-for="preview in filePreviews"
                    >
                        <button
                            class="h-[30px] aspect-square rounded-lg transition-all cursor-pointer text-lg bg-alert hover:bg-alert-dark text-white top-2 right-2 absolute"
                            @click.prevent="
                                files.splice(files.indexOf(preview), 1)
                            "
                        >
                            <i class="fas fa-times"></i>
                        </button>
                        <img
                            v-if="preview.type === 'image'"
                            :src="preview.url"
                            class="w-full h-full object-contain object-center max-h-full"
                        />
                        <i
                            v-else-if="preview.fileExtension === 'pdf'"
                            class="fas"
                            v-bind:class="`fa-file-pdf fa-5x text-slate-500`"
                        ></i>
                        <i
                            v-else-if="
                                preview.fileExtension === 'zip' ||
                                preview.fileExtension === 'rar'
                            "
                            class="fas"
                            v-bind:class="`fa-file-zipper fa-5x text-slate-500`"
                        ></i>
                        <i
                            v-else-if="
                                preview.fileExtension === 'doc' ||
                                preview.fileExtension === 'docx'
                            "
                            class="fas"
                            v-bind:class="`fa-file-word fa-5x text-slate-500`"
                        ></i>
                        <i
                            v-else-if="
                                preview.fileExtension === 'xls' ||
                                preview.fileExtension === 'xlsx' ||
                                preview.fileExtension === 'csv'
                            "
                            class="fas"
                            v-bind:class="`fa-file-excel fa-5x text-slate-500`"
                        ></i>
                        <i
                            v-else
                            class="fas"
                            v-bind:class="`fa-file fa-5x text-slate-500`"
                        ></i>
                        <p
                            v-if="preview.type !== 'image'"
                            class="text-center mt-1 text-sm text-slate-500"
                        >
                            {{ preview.fileName }}
                        </p>
                    </div>
                </div>
                <input
                    class="absolute top-0 left-0 -z-[1] opacity-0"
                    type="file"
                    :accept="acceptsFileTypes"
                    :multiple="multiple"
                    @change="onChange"
                />
            </label>
        </div>
    </modal>
</template>

<script>
import Modal from './Modal.vue';
import localization from '../../mixins/localization';

export default {
    name: 'FileSelectModal',
    props: {
        show: {
            type: Boolean,
            required: true,
        },
        loading: {
            type: Boolean,
            required: false,
            default: false,
        },
        loadingText: {
            type: String,
            required: false,
        },
        freezeBody: {
            type: Boolean,
            required: false,
            default: true,
        },
        acceptsFileTypes: {
            type: String,
            required: false,
        },
        multiple: {
            type: Boolean,
            required: false,
            default: false,
        },
        maxFileSize: {
            type: Number,
            required: false,
            default: 0,
        },
        minFiles: {
            type: Number,
            required: false,
            default: 0,
        },
        maxFiles: {
            type: Number,
            required: false,
            default: 0,
        },
        exactAmountOfFiles: {
            type: Number,
            required: false,
            default: 0,
        },
    },
    watch: {
        show(newValue, oldValue) {
            if (newValue && !oldValue) {
                this.files = [];
            }
        },
    },
    data() {
        return {
            files: [],
        };
    },
    computed: {
        title() {
            if (!this.multiple) {
                return this.trans('file_select_modal.title.one');
            }

            if (this.exactAmountOfFiles > 0) {
                return this.trans('file_select_modal.title.exact_limit', {
                    limit: this.exactAmountOfFiles,
                });
            }

            if (this.maxFiles > 0 && this.minFiles > 0) {
                return this.trans('file_select_modal.title.range_limit', {
                    min: this.minFiles,
                    max: this.maxFiles,
                });
            }

            if (this.maxFiles > 0) {
                return this.trans('file_select_modal.title.max_limit', {
                    limit: this.maxFiles,
                });
            }

            if (this.minFiles > 0) {
                return this.trans('file_select_modal.title.min_limit', {
                    limit: this.minFiles,
                });
            }

            return this.trans('file_select_modal.title.one_or_more');
        },
        actions() {
            return [
                {
                    label: this.trans('file_select_modal.actions.cancel'),
                    handler: () => {
                        this.files = [];
                        this.close();
                    },
                },
                {
                    label: this.trans('file_select_modal.actions.confirm'),
                    theme: 'primary',
                    handler: () => {
                        this.confirm();
                    },
                },
            ];
        },
        filePreviews() {
            return this.files.map((file) => {
                const fileType = file.type.split('/')[0];
                const fileExtension = file.name.split('.').pop().toLowerCase();
                const fileName = file.name.split('.').slice(0, -1).join('.');
                const isImage = fileType === 'image';
                const url = isImage ? URL.createObjectURL(file) : null;

                return {
                    type: isImage ? 'image' : fileType,
                    url,
                    fileExtension,
                    fileName,
                };
            });
        },
    },
    methods: {
        onChange(event) {
            this.files = Array.from(event.target.files);
        },
        close() {
            this.$emit('close');
        },
        confirm() {
            this.$emit('confirm', this.files);
        },
    },
    components: {
        Modal,
    },
    mixins: [localization],
};
</script>
